import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Check, Loader2, ShieldCheck,
} from 'lucide-react';
import {
  listAgentOpsQueue, resolveAgentPayout,
  getDisplayErrorMessage,
  type AgentOpsQueueRow,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { formatCommissionRow, type CommissionRow } from '@/lib/spCommissionMoney';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { AgentStatusBadge } from './AgentStatusBadge';
import { AgentReconciliationPanel } from './AgentReconciliationPanel';
import styles from './AgentOpsQueuePage.module.css';

/**
 * Agent ops attention queue (Phase 7d of the agent-commission ticket).
 *
 * Surfaces AgentPayout rows that need operator review:
 *   - status='clawback_pending' (Phase 6b refund-driven reversal
 *     failed because the agent's Connect balance was insufficient;
 *     debt recorded against Agent.outstanding_clawback_cents)
 *   - status='permanently_failed' (Phase 4 retry cron exhausted
 *     MAX_RETRIES , awaiting manual intervention)
 *   - manual_review_required=true (Phase 3e flagged an edge case
 *     like agent_not_found or no_source_charge; Phase 6b flagged
 *     a transient reversal failure)
 *
 * super_admin only. Nav-gated via requiresSuperAdmin: true on the
 * agentOpsQueue leaf. Direct-URL navigation by non-super_admin
 * renders the page but the API call 403s → error state.
 *
 * v1 ships only the "Mark resolved" row action , clears
 * manual_review_required. Phase 7d.1 will add "Retry now" and
 * "Write off clawback" once real ops feedback exists for those
 * nuanced cases.
 */

type FilterKey = 'all' | 'clawback_pending' | 'permanently_failed' | 'manual_review';

interface Props {
  /** SPA navigation callback. Optional so legacy direct-render call
   *  sites don't break; missing → anchor falls back to default
   *  href behavior (full page reload), still functionally correct. */
  onNavigate?: (to: string) => void;
}

export function AgentOpsQueuePage({ onNavigate }: Props = {}) {
  useRecentRoute('Sales Partner Queue');
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'super_admin';

  const [rows, setRows] = useState<CommissionRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [confirmTarget, setConfirmTarget] = useState<AgentOpsQueueRow | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);
  const [confirmErr, setConfirmErr] = useState('');

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const res = await listAgentOpsQueue();
      setRows(res.rows);
      setTruncated(res.truncated);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Failed to load Sales Partner queue'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      setError('Sales Partner queue is super_admin only.');
      return;
    }
    void reload();
    // reload is a stable closure on this component; intentionally
    // not in the dep array (would loop on every render).
  }, [isSuperAdmin]);

  // Client-side filter. Server returns the full attention set; the
  // chips slice locally per Phase 7d discovery §7.
  const visible = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'clawback_pending') {
      return rows.filter((r) => r.status === 'clawback_pending');
    }
    if (filter === 'permanently_failed') {
      return rows.filter((r) => r.status === 'permanently_failed');
    }
    // manual_review , rows where the flag is set regardless of status
    return rows.filter((r) => r.manual_review_required);
  }, [rows, filter]);

  const handleResolve = async (): Promise<void> => {
    if (!confirmTarget) return;
    setConfirmPending(true);
    setConfirmErr('');
    try {
      const targetId = confirmTarget.id;
      await resolveAgentPayout(targetId);
      // Optimistic removal , mirror of LockedOutDriversPage pattern.
      // The next fetch would drop the row anyway since the query
      // filters out manual_review_required=false rows that aren't
      // also clawback_pending/permanently_failed; this avoids the
      // refetch flicker.
      //
      // Edge case: a row in clawback_pending OR permanently_failed
      // status with manual_review_required=true would STILL appear
      // in the next fetch even after we clear the flag (the status
      // branch of the $or still matches). Optimistic removal here
      // is wrong for those , instead, update the row in-place to
      // unset manual_review_required so the operator can see the
      // remaining attention signal (status). UI mirrors server.
      setRows((prev) => prev.map((r) => {
        if (r.id !== targetId) return r;
        // If the row was attention-worthy only via the
        // manual_review_required flag, drop it. Otherwise keep
        // visible with the flag cleared.
        const stillAttention =
          r.status === 'clawback_pending' || r.status === 'permanently_failed';
        if (stillAttention) {
          return { ...r, manual_review_required: false };
        }
        return null as unknown as AgentOpsQueueRow;
      }).filter(Boolean));
      setConfirmTarget(null);
    } catch (err) {
      setConfirmErr(await getDisplayErrorMessage(err, 'Failed to resolve'));
    } finally {
      setConfirmPending(false);
    }
  };

  return (
    <div className={styles.page}>
      {isSuperAdmin && <AgentReconciliationPanel />}
      <header className={styles.header}>
        <span className={styles.headerIcon}><AlertCircle size={20} /></span>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Sales Partner Queue</h1>
          <p className={styles.subtitle}>
            Sales Partner payouts that need operator attention. Includes
            clawback-pending (reversal failed, debt recorded),
            permanently-failed (retry exhausted), and rows flagged
            for manual review by the money-flow handlers.
          </p>
        </div>
      </header>

      {/* Filter chips */}
      {isSuperAdmin && (
        <div className={styles.toolbar}>
          <div className={styles.filterChips}>
            {([
              { key: 'all', label: 'All' },
              { key: 'clawback_pending', label: 'Clawback Pending' },
              { key: 'permanently_failed', label: 'Permanently Failed' },
              { key: 'manual_review', label: 'Manual Review' },
            ] as const).map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.filterChip} ${filter === f.key ? styles.filterChipActive : ''}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {truncated && (
            <p className={styles.truncatedHint}>
              Showing first 200 rows. Queue exceeds the soft cap ,
              resolve some attention items to see the rest.
            </p>
          )}
        </div>
      )}

      {/* Loading + error states */}
      {loading && (
        <div className={styles.empty}>
          <Loader2 size={18} className={styles.spin} />
          <span>Loading ops queue…</span>
        </div>
      )}

      {!loading && error && (
        <div className={styles.error}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className={styles.empty}>
          {rows.length === 0 ? (
            <>
              <Check size={32} className={styles.emptyIconOk} />
              <p className={styles.emptyTitle}>Nothing needs attention</p>
              <p className={styles.emptyBody}>
                No Sales Partner payouts need operator review. The retry cron
                and refund handlers are keeping pace.
              </p>
            </>
          ) : (
            <>
              <ShieldCheck size={20} className={styles.emptyIcon} />
              <p className={styles.emptyBody}>
                No Sales Partner payouts match the current filter.
              </p>
            </>
          )}
        </div>
      )}

      {/* Table */}
      {!loading && !error && visible.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Sales Partner</th>
                <th>Session</th>
                <th>Status</th>
                <th>Gross / Net</th>
                <th>Clawback / Recorded</th>
                <th>Partner Outstanding</th>
                <th>Last error</th>
                <th>Created</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className={styles.agentCell}>
                      <span className={styles.agentName}>
                        {/* Phase 7e , deep link to per-agent history.
                            Plain <a> with href; routing is hash/path-
                            based in this app and the App.tsx switch
                            handles /agents/:id navigation. Only links
                            when agent_name is hydrated (not deleted
                            out-of-band). */}
                        {r.agent_name ? (
                          <a
                            href={`/agents/${r.agent_id}`}
                            className={styles.agentLink}
                            onClick={(e) => {
                              if (onNavigate) {
                                e.preventDefault();
                                onNavigate(`/agents/${r.agent_id}`);
                              }
                            }}
                          >
                            {r.agent_name}
                          </a>
                        ) : (
                          <span className={styles.muted}>(deleted)</span>
                        )}
                        {r.agent_active === false && (
                          <span className={styles.toggleHint}> (inactive)</span>
                        )}
                      </span>
                      {r.agent_email && (
                        <span className={styles.agentEmail}>{r.agent_email}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <code className={styles.idCode} title={r.session_id}>
                      {r.session_started_at
                        ? new Date(r.session_started_at).toLocaleDateString()
                        : r.session_id.slice(-8)}
                    </code>
                  </td>
                  <td>
                    <AgentStatusBadge
                      status={r.status}
                      manualReview={r.manual_review_required}
                    />
                  </td>
                  <td>
                    <div className={styles.numStack}>
                      <span>{formatCommissionRow(r)}</span>
                      <span className={styles.muted}>
                        {formatCommissionRow(r, r.net_transfer_cents)} net
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={styles.numStack}>
                      <span>{formatCommissionRow(r, r.clawback_applied_cents)}</span>
                      {r.clawback_debt_recorded_cents > 0 && (
                        <span className={styles.muted}>
                          {formatCommissionRow(r, r.clawback_debt_recorded_cents)} debt
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {typeof r.agent_outstanding_clawback_cents === 'number'
                      ? (
                        <span className={
                          r.agent_outstanding_clawback_cents > 0
                            ? styles.chipWarn
                            : styles.muted
                        }>
                          ${(Math.max(0, r.agent_outstanding_clawback_cents) / 100).toFixed(2)}
                        </span>
                      )
                      : <span className={styles.muted}>Not available</span>}
                  </td>
                  <td>
                    {r.last_error ? (
                      <span className={styles.errorCell} title={r.last_error}>
                        {r.last_error.length > 80
                          ? `${r.last_error.slice(0, 80)}…`
                          : r.last_error}
                      </span>
                    ) : (
                      <span className={styles.muted}>Not available</span>
                    )}
                  </td>
                  <td>
                    <span title={new Date(r.created_at).toLocaleString()}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className={styles.rowActions}>
                    {r.manual_review_required ? (
                      <button
                        type="button"
                        className={styles.iconBtn}
                        title="Mark resolved"
                        onClick={() => setConfirmTarget(r)}
                      >
                        <Check size={13} /> Resolve
                      </button>
                    ) : (
                      <span className={styles.muted}>Not available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm-resolve modal */}
      {confirmTarget && (
        <Modal
          isOpen
          onClose={() => {
            if (!confirmPending) {
              setConfirmTarget(null);
              setConfirmErr('');
            }
          }}
          title="Mark resolved?"
          size="sm"
        >
          <div className={styles.confirmBody}>
            {confirmErr && <div className={formStyles.formError}>{confirmErr}</div>}
            <p>
              Clear the <code>manual_review_required</code> flag on
              this payout row.
            </p>
            <p className={styles.confirmHint}>
              {confirmTarget.status === 'clawback_pending'
                || confirmTarget.status === 'permanently_failed' ? (
                  <>
                    Note: the row will remain in the queue because its
                    status (<code>{confirmTarget.status}</code>) is also
                    an attention signal. Use Phase 7d.1&rsquo;s retry /
                    write-off actions (when shipped) to resolve the
                    status-based attention.
                  </>
                ) : (
                  <>
                    The row will drop from the queue on next refresh.
                  </>
                )}
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setConfirmTarget(null)}
                disabled={confirmPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={formStyles.btnPrimary}
                onClick={() => void handleResolve()}
                disabled={confirmPending}
              >
                {confirmPending
                  ? <><Loader2 size={12} className={styles.spin} /> Resolving…</>
                  : <>Mark resolved</>}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// StatusBadge extracted to ./AgentStatusBadge.tsx in Phase 7e so
// AgentDetailPage can share the same status → color/label mapping.
// Source of truth lives there; both consumers (this file +
// AgentDetailPage) import from the shared module.
